import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { enfermeriaApi, IndicacionCreatePayload } from '../lib/api'
import AppHeader from '../components/AppHeader'
import { useToast } from '../components/Toast'

interface Dieta {
  id: number
  codigo: string
  nombre: string
}

interface Tiempo {
  id: number
  codigo: string
  nombre: string
  orden: number
  es_refrigerio: boolean
}

interface IndicacionHoy {
  indicacion_id: number
  comida_elegida_id: number
  paciente_nombre: string
  habitacion: string
  dieta_codigo: string
  dieta_nombre: string
  tiempo_codigo: string
  tiempo_nombre: string
  estado: string
  hora_indicacion: string
  indicacion_dr: string | null
}

// 24 habitaciones CM (sede_id=1) según RUNBOOK_SPRINT_0
const HABITACIONES = Array.from({ length: 24 }, (_, i) =>
  String(i + 1).padStart(3, '0'),
)

const DIAS_SEMANA = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
]

function diaSemanaHoy(): number {
  const js = new Date().getDay()
  return js === 0 ? 7 : js
}

export default function EnfermeriaIndicacion() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()

  // Catálogos
  const [dietas, setDietas] = useState<Dieta[]>([])
  const [tiempos, setTiempos] = useState<Tiempo[]>([])
  const [catalogosListos, setCatalogosListos] = useState(false)

  // Form
  const [paciente_nombre, setPacienteNombre] = useState('')
  const [paciente_documento, setPacienteDocumento] = useState('')
  const [habitacion_codigo, setHabitacion] = useState('001')
  const [dieta_codigo, setDietaCodigo] = useState('')
  const [tiempo_codigo, setTiempoCodigo] = useState('')
  const [dia_semana, setDiaSemana] = useState(diaSemanaHoy())
  const [indicacion_dr, setIndicacionDr] = useState('')
  const [notas, setNotas] = useState('')

  // Cobertura del menú vigente para el día elegido (null = sin cargar).
  // Solo se ofrecen tiempos que la dieta seleccionada desarrolla ese día
  // (ej. refrigerios existen solo en Trasplantados, Diabética y Libre de gluten).
  const [cobertura, setCobertura] = useState<Set<string> | null>(null)

  // UI state
  const [submitting, setSubmitting] = useState(false)

  // Lista del día
  const [indicaciones, setIndicaciones] = useState<IndicacionHoy[]>([])
  const [loadingLista, setLoadingLista] = useState(true)

  // Cargar catálogos al montar
  useEffect(() => {
    let cancelado = false
    Promise.all([enfermeriaApi.dietas(), enfermeriaApi.tiempos()])
      .then(([ds, ts]) => {
        if (cancelado) return
        setDietas(ds)
        setTiempos(ts.sort((a: Tiempo, b: Tiempo) => a.orden - b.orden))
        if (ds.length > 0) setDietaCodigo(ds[0].codigo)
        if (ts.length > 0) setTiempoCodigo(ts[0].codigo)
        setCatalogosListos(true)
      })
      .catch((err) => {
        showToast(`No se cargaron catálogos: ${err.message}`, 'error')
      })
    return () => {
      cancelado = true
    }
  }, [showToast])

  // Recargar cobertura al cambiar el día
  useEffect(() => {
    let cancelado = false
    setCobertura(null)
    enfermeriaApi
      .cobertura(dia_semana)
      .then((res) => {
        if (cancelado) return
        setCobertura(new Set(res.celdas.map((c) => `${c.dieta_codigo}|${c.tiempo_codigo}`)))
      })
      .catch(() => {
        // Fallback sin filtro: el backend valida igual al crear.
        if (!cancelado) setCobertura(null)
      })
    return () => {
      cancelado = true
    }
  }, [dia_semana])

  // Tiempos que la dieta seleccionada desarrolla el día elegido
  const tiemposDisponibles = useMemo(() => {
    if (cobertura === null || !dieta_codigo) return tiempos
    return tiempos.filter((t) => cobertura.has(`${dieta_codigo}|${t.codigo}`))
  }, [tiempos, cobertura, dieta_codigo])

  // Si el tiempo elegido dejó de estar disponible (cambió dieta o día),
  // saltar al primero válido para no mandar una combinación imposible.
  useEffect(() => {
    if (tiemposDisponibles.length === 0) return
    if (!tiemposDisponibles.some((t) => t.codigo === tiempo_codigo)) {
      setTiempoCodigo(tiemposDisponibles[0].codigo)
    }
  }, [tiemposDisponibles, tiempo_codigo])

  // Polling de la lista del día
  async function refreshLista() {
    try {
      const data = await enfermeriaApi.indicacionesHoy()
      setIndicaciones(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingLista(false)
    }
  }

  useEffect(() => {
    refreshLista()
    const interval = setInterval(refreshLista, 30_000)
    return () => clearInterval(interval)
  }, [])

  const formularioCompleto = useMemo(
    () =>
      paciente_nombre.trim().length >= 3 &&
      habitacion_codigo &&
      dieta_codigo &&
      tiempo_codigo &&
      indicacion_dr.trim().length >= 2,
    [paciente_nombre, habitacion_codigo, dieta_codigo, tiempo_codigo, indicacion_dr],
  )

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!formularioCompleto || submitting) return

    setSubmitting(true)

    const payload: IndicacionCreatePayload = {
      habitacion_codigo,
      paciente_nombre: paciente_nombre.trim(),
      paciente_documento: paciente_documento.trim() || null,
      dieta_codigo,
      tiempo_codigo,
      dia_semana,
      indicacion_dr: indicacion_dr.trim(),
      notas: notas.trim() || null,
    }

    try {
      const res = await enfermeriaApi.crearIndicacion(payload)
      const aviso = res.warnings?.length ? ` (${res.warnings.join(' · ')})` : ''
      showToast(
        `Indicación #${res.indicacion_id} creada para ${res.paciente_nombre} (Hab. ${res.habitacion})${aviso}`,
        'success',
      )
      // Reset campos del paciente — mantener dieta/tiempo/dia/dr para indicaciones en lote
      setPacienteNombre('')
      setPacienteDocumento('')
      setNotas('')
      await refreshLista()
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message
      showToast(detail, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        title="Enfermería — Crear indicación"
        user={user}
        onLogout={logout}
      />

      <main className="p-4 space-y-6 max-w-2xl mx-auto">
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm flex items-center justify-between">
          <span className="text-blue-900">
            ¿Necesitas dietas distintas por tiempo para el mismo paciente?
          </span>
          <Link
            to="/enfermeria/matriz"
            className="text-blue-700 underline font-medium shrink-0 ml-3"
          >
            Modo matriz →
          </Link>
        </div>

        {!catalogosListos && <p className="text-gray-500">Cargando catálogos...</p>}

        {catalogosListos && (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Paciente (nombre completo)</label>
              <input
                type="text"
                value={paciente_nombre}
                onChange={(e) => setPacienteNombre(e.target.value)}
                required
                minLength={3}
                maxLength={200}
                placeholder="Ej. Pedro Méndez Romero"
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                N° expediente / DUI / pasaporte (opcional)
              </label>
              <input
                type="text"
                value={paciente_documento}
                onChange={(e) => setPacienteDocumento(e.target.value)}
                maxLength={50}
                placeholder="Recomendado para evitar duplicados al re-internar"
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Habitación</label>
                <select
                  value={habitacion_codigo}
                  onChange={(e) => setHabitacion(e.target.value)}
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
                >
                  {HABITACIONES.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Día semana</label>
                <select
                  value={dia_semana}
                  onChange={(e) => setDiaSemana(Number(e.target.value))}
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
                >
                  {DIAS_SEMANA.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Dieta</label>
              <select
                value={dieta_codigo}
                onChange={(e) => setDietaCodigo(e.target.value)}
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              >
                {dietas.map((d) => (
                  <option key={d.id} value={d.codigo}>
                    {d.codigo} — {d.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tiempo de comida</label>
              <select
                value={tiempo_codigo}
                onChange={(e) => setTiempoCodigo(e.target.value)}
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              >
                {tiemposDisponibles.map((t) => (
                  <option key={t.id} value={t.codigo}>
                    {t.codigo} — {t.nombre}
                  </option>
                ))}
              </select>
              {cobertura !== null && tiemposDisponibles.length < tiempos.length && (
                <p className="text-xs text-gray-500 mt-1">
                  Solo se muestran los tiempos con menú para esta dieta el día elegido.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Médico que indica</label>
              <input
                type="text"
                value={indicacion_dr}
                onChange={(e) => setIndicacionDr(e.target.value)}
                required
                minLength={2}
                maxLength={200}
                placeholder="Ej. Dr. Ramírez Cardiología"
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notas clínicas (opcional)</label>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                maxLength={2000}
                rows={2}
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>

            <button
              type="submit"
              disabled={!formularioCompleto || submitting}
              className="w-full bg-avante-primary text-white py-3 rounded font-semibold disabled:opacity-50"
            >
              {submitting ? 'Creando...' : 'Crear indicación'}
            </button>
          </form>
        )}

        <section>
          <h2 className="font-semibold text-gray-700 mb-2">Indicaciones de hoy</h2>
          {loadingLista && <p className="text-gray-500 text-sm">Cargando...</p>}
          {!loadingLista && indicaciones.length === 0 && (
            <div className="bg-white rounded-lg shadow p-4 text-center text-gray-500 text-sm">
              Sin indicaciones registradas hoy.
            </div>
          )}
          {indicaciones.map((i) => (
            <div key={i.indicacion_id} className="bg-white rounded-lg shadow p-3 mb-2">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="font-semibold text-sm">
                    Hab. {i.habitacion} — {i.paciente_nombre}
                  </p>
                  <p className="text-xs text-gray-600">
                    {i.dieta_codigo} · {i.tiempo_codigo} · {new Date(i.hora_indicacion).toLocaleTimeString()}
                  </p>
                  {i.indicacion_dr && (
                    <p className="text-xs text-gray-500 italic">{i.indicacion_dr}</p>
                  )}
                </div>
                <span className="text-xs px-2 py-1 rounded bg-gray-200">{i.estado}</span>
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  )
}
